import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { DIAGNOSTIC } from "@/constants/testIds";
import { QUESTIONS } from "@/features/diagnostic/config";
import { useDiagnostic } from "@/features/diagnostic/DiagnosticContext";
import { GhostButton, PrimaryButton } from "@/components/site/Primitives";

export function QuestionStep({ onContinue, pending, error }) {
  const q = QUESTIONS[0];
  const { state, setAnswer } = useDiagnostic();
  const [touched, setTouched] = useState(false);
  const ref = useRef(null);
  const value = state.answers[q.id] ?? "";
  const empty = value.trim().length === 0;

  useEffect(() => { ref.current?.focus({ preventScroll: true }); }, []);

  const submit = (e) => {
    e.preventDefault();
    if (empty) return setTouched(true);
    onContinue();
  };

  return (
    <form onSubmit={submit} noValidate className="fa-rise">
      <p className="mb-4 text-[14px] text-subtle" data-testid="diagnostic-received-note">Thanks — we’ve received your business need.</p>
      <h1 data-testid={DIAGNOSTIC.intro} className="font-display text-3xl font-light tracking-[-0.03em] text-fg sm:text-4xl">
        Let’s understand this better.
      </h1>
      <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.22em] text-subtle">Question 1 of {QUESTIONS.length}</p>
      <label htmlFor="answer" data-testid={DIAGNOSTIC.question} className="mt-4 block text-xl font-medium leading-snug text-fg md:text-2xl">
        {q.prompt}
      </label>
      <p id="answer-help" className="mt-3 text-[15px] text-subtle">{q.helper}</p>
      <textarea id="answer" ref={ref} rows={5} value={value} data-testid={DIAGNOSTIC.answer}
        aria-describedby="answer-help answer-error" placeholder={q.placeholder}
        onChange={(e) => { setAnswer(q.id, e.target.value); setTouched(false); }}
        className="fa-field mt-6 w-full resize-y rounded-xl border border-fg/10 bg-fg/[0.03] px-5 py-4 text-[15px] leading-relaxed text-fg placeholder:text-fg/30" />
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
        <PrimaryButton type="submit" disabled={pending} aria-busy={pending} data-testid={DIAGNOSTIC.continueButton}>{pending ? "Saving…" : "Save and continue"} {!pending && <ArrowRight size={16} aria-hidden />}</PrimaryButton>
        <p id="answer-error" role="alert" aria-live="polite" className="text-[14px] text-danger">
          {touched && empty ? "Add a short answer to continue." : error || ""}
        </p>
      </div>
    </form>
  );
}

export function ReviewStep({ onEditAnswer, onSend, pending, error }) {
  return (
    <div data-testid={DIAGNOSTIC.complete} className="fa-rise">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-tone">Final step</p>
      <h1 className="mt-5 font-display text-3xl font-light tracking-[-0.03em] text-fg sm:text-4xl">Send your request to our team.</h1>
      <div className="mt-6 max-w-2xl space-y-4 text-[15px] leading-relaxed text-subtle md:text-base">
        <p>Your answer has been saved. When you send your request, a person on our team reviews your business need and answer before anything is recommended.</p>
        <p>No AI analysis, pricing or timeline is produced automatically.</p>
      </div>
      {error && <p role="alert" data-testid="diagnostic-send-error" className="mt-6 max-w-2xl rounded-xl border border-fg/20 px-4 py-3 text-[14px] text-fg">{error}</p>}
      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <PrimaryButton onClick={onSend} disabled={pending} aria-busy={pending} data-testid="diagnostic-send-button">{pending ? "Sending…" : "Send my request"}</PrimaryButton>
        <GhostButton onClick={onEditAnswer} disabled={pending} data-testid={DIAGNOSTIC.editAnswer}>Edit my answer</GhostButton>
      </div>
    </div>
  );
}

export function SentStep({ onHome, onStartOver }) {
  return (
    <div data-testid="diagnostic-request-sent" role="status" className="fa-rise">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-tone">Request sent</p>
      <h1 className="mt-5 font-display text-3xl font-light tracking-[-0.03em] text-fg sm:text-4xl">Your request is ready for our team to review.</h1>
      <div className="mt-6 max-w-2xl space-y-4 text-[15px] leading-relaxed text-subtle md:text-base">
        <p>A member of our team will review what you shared and contact you using the details you provided if we can help.</p>
        <p>Nothing has been decided automatically, and no automated email has been sent.</p>
      </div>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <PrimaryButton onClick={onHome} data-testid={DIAGNOSTIC.returnHome}>Back to home</PrimaryButton>
        <GhostButton onClick={onStartOver} data-testid={DIAGNOSTIC.startOver} className="border-transparent text-subtle">Start a new request</GhostButton>
      </div>
    </div>
  );
}
